/* eslint-disable max-lines */
/* eslint-disable react/jsx-no-target-blank */
import { AmericanFlag, SpanishFlag } from './assets';
import { ArrowUpRight, ChevronDown, LogOut } from 'lucide-react';
import {
  DropdownMenuContentChildren,
  Header,
  HeaderElement,
  Text
} from '@pulsarml/avalon-ui';
import { I18n, Translate } from '@utils/i18n';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CompanySelection from '_clean-code/components/CompanySelection';
import MachineGroupSelector from '_clean-code/components/MachineGroupSelector';
import WhatsNew from '../WhatsNew';
import cn from 'classnames';
import { sendEventTrack } from '@utils/analytics';
import { setTranslationLocale } from 'ducks/i18n/actions';
import styles from './index.module.scss';
import { toggleSidebarCollapsed } from 'ducks/config/actions';
import { useNavigate } from 'react-router-dom';
import { userSelector } from 'ducks/main-dashboard/selectors';
import { useAuth } from '_clean-code/api/hooks/useAuth';

interface AppTopBarProps {
  classNames?: string;
  title?: string;
}

const MetabaseRoutesRegex = /\/analytics\/.*-\d+/;

function AppTopBar(props: AppTopBarProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isMachineGroupOpen, setIsMachineGroupOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(true);
  const currentUser = useSelector(userSelector);
  const { logout } = useAuth();

  const route = window.location.pathname;
  const metabaseRoutes = MetabaseRoutesRegex.test(route);

  return (
    <>
      <Header
        title={props.title!}
        userName={currentUser.name ?? ''}
        onHamburgerClick={() => {
          dispatch(toggleSidebarCollapsed());
        }}
        onLogoClick={() => {
          navigate('/');
        }}
        onOpenChange={(open) => {
          if (open) {
            setIsMachineGroupOpen(false);
            setShowModal(false);
          }
        }}
      >
        <HeaderElement>
          <div className={styles.rightPanel}>
            <CompanySelection className={styles.companySelection} />
            {!metabaseRoutes && (
              <div className={styles.machineGroup}>
                <MachineGroupSelector
                  classNames={styles.machineGroupSelector}
                  isOpen={isMachineGroupOpen}
                  setIsOpen={setIsMachineGroupOpen}
                />
              </div>
            )}
          </div>
        </HeaderElement>
        <DropdownMenuContentChildren>
          <div
            className={cn(
              'tw-flex tw-flex-col tw-min-w-[200px] tw-max-w-[250px]',
              styles.dropdownContent,
              isLanguageMenuOpen ? 'tw-max-h-[316px]' : 'tw-max-h-[289px]'
            )}
          >
            <div className="tw-flex tw-flex-col tw-gap-1 tw-pt-16px tw-px-12px tw-pb-8px tw-text-ellipsis tw-text-nowrap">
              <Text
                variant="normal"
                className="tw-font-bold tw-text-ellipsis tw-w-full"
                lineHeight="20px"
              >
                {currentUser.name}
              </Text>
              <Text
                variant="label"
                className="tw-text-sm tw-text-[#6B7280] tw-text-ellipsis tw-w-full"
                lineHeight="16px"
              >
                {currentUser.email}
              </Text>
            </div>
            <div
              className="tw-py-12px tw-cursor-pointer tw-px-12px"
              onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                <Text variant="normal" className="tw-text-sm" lineHeight="20px">
                  <Translate value="language" />
                </Text>
                <ChevronDown
                  className={cn(
                    'tw-h-4 tw-w-4',
                    styles.chevron,
                    isLanguageMenuOpen && styles.rotate
                  )}
                />
              </div>
            </div>
            {isLanguageMenuOpen && (
              <div className="tw-px-12px">
                <div
                  className={cn(
                    'tw-py-8px tw-px-12px tw-flex tw-justify-between tw-items-center tw-gap-8px tw-cursor-pointer tw-rounded-sm',
                    I18n._locale === 'us' && 'tw-bg-gray-100'
                  )}
                  onClick={() => {
                    sendEventTrack('Switched Language', {
                      language: 'us',
                      companyName: currentUser.companies?.[0]?.name
                    });
                    setTranslationLocale('us');
                    localStorage.setItem('i18n-locale', 'us');
                    window.location.reload();
                  }}
                >
                  <AmericanFlag />
                  <div className="tw-flex-1">
                    <Text
                      variant="label"
                      className="tw-text-sm tw-text-[#6B7280]"
                      lineHeight="20px"
                    >
                      <Translate value="english" />
                    </Text>
                  </div>
                </div>
                <div
                  className={cn(
                    'tw-py-8px tw-px-12px tw-flex tw-justify-between tw-items-center tw-gap-8px tw-cursor-pointer tw-rounded-sm',
                    I18n._locale === 'es' && 'tw-bg-gray-100'
                  )}
                  onClick={() => {
                    sendEventTrack('Switched Language', {
                      language: 'es',
                      companyName: currentUser.companies?.[0]?.name
                    });
                    setTranslationLocale('es');
                    localStorage.setItem('i18n-locale', 'es');
                    window.location.reload();
                  }}
                >
                  <SpanishFlag />
                  <div className="tw-flex-1">
                    <Text
                      variant="label"
                      className="tw-text-sm tw-text-[#6B7280]"
                      lineHeight="16px"
                    >
                      <Translate value="spanish" />
                    </Text>
                  </div>
                </div>
              </div>
            )}
            <div
              className="tw-py-12px tw-cursor-pointer tw-px-12px"
              onClick={() => {
                navigate('/create-ticket');
              }}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                <Text variant="normal" className="tw-text-sm" lineHeight="16px">
                  <Translate value="support_center" />
                </Text>
                <ArrowUpRight className="tw-h-4 tw-w-4" />
              </div>
            </div>
            <a
              href="https://pulsarml.zendesk.com/hc/es"
              target="_blank"
              className="tw-block tw-py-12px tw-cursor-pointer tw-border-b tw-border-b-gray-200 tw-px-12px tw-no-underline tw-text-inherit active:tw-text-black hover:tw-text-black hover:tw-no-underline"
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                <Text variant="normal" className="tw-text-sm" lineHeight="20px">
                  <Translate value="help_and_docs" />
                </Text>
                <ArrowUpRight className="tw-h-4 tw-w-4" />
              </div>
            </a>
            <div
              className="tw-py-12px tw-cursor-pointer tw-px-12px"
              onClick={() => logout.mutate()}
            >
              <div className="tw-flex tw-items-center tw-justify-start tw-gap-8px">
                <LogOut className="tw-h-4 tw-w-4" />
                <Text variant="normal" className="tw-text-sm" lineHeight="20px">
                  <Translate value="logout" />
                </Text>
              </div>
            </div>
          </div>
        </DropdownMenuContentChildren>
      </Header>
      <WhatsNew showModal={showModal} setShowModal={setShowModal} />
    </>
  );
}

export default AppTopBar;
